import { useState, useEffect } from 'react';
import io from 'socket.io-client';
import { toast } from 'react-toastify';
import { getUnpaidOrders, generatePaymentLink } from './MonthlyUnpaidService';

const PAGE_SIZE = 50;

const useMonthlyUnpaid = (asOfDate) => {
  const [data, setData] = useState({
    customers: [],
    pagination: null
  });
  const [loading, setLoading] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('paymentNeeded');
  // Customers who paid AFTER the current list was fetched (webhook socket event or a
  // refused generate). Their rows highlight and their generate button locks — no
  // auto-refetch, this page's query is heavy. Cleared whenever fresh data arrives.
  const [paidCustomerIds, setPaidCustomerIds] = useState(() => new Set());

  const fetchUnpaidOrders = async () => {
    if (!asOfDate) return;

    setLoading(true);
    try {
      const response = await getUnpaidOrders(asOfDate, { page, pageSize: PAGE_SIZE, status });
      setData(response.data);
      setPaidCustomerIds(new Set()); // fresh data supersedes the live paid-marks
    } catch (error) {
      toast.error('Failed to fetch unpaid orders');
      console.error(error);
      setData({
        customers: [],
        pagination: null
      });
    } finally {
      setLoading(false);
    }
  };

  // Change the status filter and jump back to the first page (batched, single fetch).
  const changeStatus = (newStatus) => {
    setStatus(newStatus);
    setPage(1);
  };

  const handleGeneratePaymentLink = async (customerId) => {
    // The button is disabled for marked rows; this guards direct calls too.
    if (paidCustomerIds.has(customerId)) {
      toast.error('ลูกค้ารายนี้ชำระแล้ว — กดรีเฟรชเพื่ออัปเดตรายการ');
      return;
    }
    setGeneratingLink(true);
    // Open the tab NOW, inside the click gesture — a window.open after the await
    // would be popup-blocked. It's pointed at the link once generated.
    const newTab = window.open('', '_blank');
    try {
      const response = await generatePaymentLink(customerId);
      const link = response.paymentLink || response.data?.paymentLink;

      if (link) {
        if (newTab) newTab.location = link;
        // Still copy — the admin usually pastes the link to the customer. May fail
        // silently when the new tab steals focus; the open is the primary action.
        try { await navigator.clipboard.writeText(link); } catch { /* focus lost */ }
        toast.success('เปิด Payment link ในแท็บใหม่แล้ว (คัดลอกลิงก์ให้แล้วด้วย)');
      } else {
        // No link = the backend recomputed fresh (isFullyPaidToCompany + live payments)
        // and found nothing left to pay — the row on screen is stale. This used to
        // toast SUCCESS, making an admin on a stale list think a link existed. Mark
        // the row as paid instead of refetching (heavy query).
        if (newTab) newTab.close();
        setPaidCustomerIds((prev) => new Set(prev).add(customerId));
        toast.error('ลูกค้ารายนี้ไม่มียอดต้องจ่ายแล้ว (อาจชำระไปแล้ว) — ไม่ได้สร้างลิงก์ใหม่');
      }
    } catch (error) {
      if (newTab) newTab.close();
      toast.error('Failed to generate payment link');
      console.error(error);
    } finally {
      setGeneratingLink(false);
    }
  };

  useEffect(() => {
    fetchUnpaidOrders();
  }, [asOfDate, page, status]);

  // Live paid-marks: the Beam webhook emits when a company order payment completes.
  // No refetch (heavy query) — just mark the customer so their rows highlight and
  // the generate button locks until the admin refreshes.
  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, {
      withCredentials: true,
    });
    socket.on('company_payment_completed', ({ customerId } = {}) => {
      if (customerId) setPaidCustomerIds((prev) => new Set(prev).add(customerId));
    });
    return () => { socket.disconnect(); };
  }, []);

  return {
    data,
    loading,
    generatingLink,
    paidCustomerIds,
    refetch: fetchUnpaidOrders,
    handleGeneratePaymentLink,
    page,
    setPage,
    status,
    changeStatus,
    pagination: data.pagination
  };
};

export default useMonthlyUnpaid;
